import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormsModule,
  FormGroup,
  Validators,
  FormBuilder,
  FormArray,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';

@Component({
  selector: 'app-add-promotion-modal',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatIcon,
    CommonModule,
    FormsModule,
    NgMultiSelectDropDownModule,
    ReactiveFormsModule,
  ],
  templateUrl: './add-promotion-modal.component.html',
  styleUrl: './add-promotion-modal.component.css',
})
export class AddPromotionModalComponent {
  // Inject dialog reference and data
  readonly dialogRef = inject(MatDialogRef<AddPromotionModalComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);

  dayList: any = [
    { day: 'Mon', isChecked: false },
    { day: 'Tue', isChecked: false },
    { day: 'Wed', isChecked: false },
    { day: 'Thu', isChecked: false },
    { day: 'Fri', isChecked: false },
    { day: 'Sat', isChecked: false },
    { day: 'Sun', isChecked: false },
  ];

  dropdownList: any = [];
  selectedItems: any = [];
  dropdownSettings: any = {};

  selectedScope: string = 'item';
  promotionForm: FormGroup;

  constructor(private fb: FormBuilder) {
    if (this.data && this.data.promotion) {
      this.promotionForm = this.fb.group({
        promotionName: [
          this.data.promotion.name,
          [Validators.required, Validators.minLength(3), this.uniquePromotionNameValidator()],
        ],
        type: [this.data.promotion.type.toLowerCase(), Validators.required],
        discount: [
          this.data.promotion.type == 'fixed'
            ? this.data.promotion.promotion_price
            : this.data.promotion.promotion_percentage,
          [Validators.required, Validators.min(0)],
        ],
        scope: [this.data.promotion.scope.toLowerCase(), Validators.required],
        items: [this.getItems(this.data.promotion.scope_options), Validators.required],
        startDate: [this.data.promotion.promotion_start_date, Validators.required],
        endDate: [this.data.promotion.promotion_end_date, Validators.required],
        startTime: ['', Validators.required],
        endTime: [this.data.promotion.promotion_end_time, Validators.required],
        activeDays: this.fb.array([], Validators.required),
      });
      this.selectedItems = this.getItems(this.data.promotion.scope_options);
      const activeDays = this.promotionForm.get('activeDays') as FormArray;
      this.data.promotion.active_days.forEach((day: string) => {
        let dayObj = this.dayList.find((d: any) => d.day.toLowerCase() === day.toLowerCase());
        if (dayObj) {
          dayObj.isChecked = true;
          activeDays.push(this.fb.control(dayObj.day));
        }
      });
    } else {
      this.promotionForm = this.fb.group(
        {
          promotionName: [
            '',
            [Validators.required, Validators.minLength(3), this.uniquePromotionNameValidator()],
          ],
          type: ['0', Validators.required],
          discount: ['', [Validators.required, Validators.min(0)]],
          scope: ['item', Validators.required],
          items: [[], Validators.required],
          startDate: ['', Validators.required],
          endDate: ['', Validators.required],
          startTime: ['', Validators.required],
          endTime: ['', Validators.required],
          activeDays: this.fb.array([], Validators.required),
        },
        { validators: [this.dateRangeValidator()] },
      );
    }
  }

  uniquePromotionNameValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = control.value?.trim().toLowerCase();
      if (
        this.data.allPromotions.find((f: any) => control.value == f.name) &&
        this.data.promotion.name != control.value
      ) {
        return { duplicateName: true };
      }
      return null;
    };
  }

  dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startDate = group.get('startDate')?.value;
      const endDate = group.get('endDate')?.value;

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start >= end) {
          return { invalidDateRange: 'Start date must be less than end date.' }; // Error message
        }
      }
      return null; // Valid if conditions are met
    };
  }

  timeRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startTime = group.get('startTime')?.value;
      const endTime = group.get('endTime')?.value;

      if (startTime && endTime) {
        const [startHours, startMinutes] = startTime.split(':').map(Number);
        const [endHours, endMinutes] = endTime.split(':').map(Number);

        const start = new Date();
        start.setHours(startHours, startMinutes);

        const end = new Date();
        end.setHours(endHours, endMinutes);

        if (start >= end) {
          return { invalidTimeRange: 'Start time must be less than end time.' }; // Error message
        }
      }
      return null; // Valid if conditions are met
    };
  }

  ngOnInit() {
    if (this.data && this.data.menus && this.data.menus.length > 0) {
      this.data.menus.forEach((menu: any) => {
        if (menu.promotion == null || menu.promotion == undefined)
          this.dropdownList.push({ item_id: menu.id, item_text: menu.name });
      });
    }

    this.dropdownSettings = {
      singleSelection: false,
      idField: 'item_id',
      textField: 'item_text',
      selectAllText: 'Select All',
      unSelectAllText: 'UnSelect All',
      itemsShowLimit: 2,
      allowSearchFilter: true,
      defaultOpen: false,
    };
  }

  getItems(items: any) {
    let itemList: any = [];
    items
      .filter((f: any) => f)
      .forEach((item: any) => {
        itemList.push({ item_id: item.id, item_text: item.name });
      });
    return itemList;
  }

  onPromoScopeChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    this.selectedScope = selectedValue;
  }

  onItemSelect(event: any): void {
    const selectedItems = this.promotionForm.get('items')?.value || [];
    this.promotionForm.get('items')?.setValue([...selectedItems, event.value]);
  }

  onSelectAll(items: any): void {
    this.promotionForm.get('items')?.setValue(items.map((item: any) => item.value));
  }

  addNewPromotion() {
    if (this.promotionForm.valid) {
      const formValues = this.promotionForm.value;

      const promotionData: any = {
        name: formValues.promotionName,
        promotion_start_date: formValues.startDate,
        promotion_end_date: formValues.endDate,
        active_days: formValues.activeDays.map((day: string) => day.toLowerCase().slice(0, 3)),
        promotion_start_time: formValues.startTime,
        promotion_end_time: formValues.endTime,
        scope: formValues.scope.charAt(0).toUpperCase() + formValues.scope.slice(1),
        scope_options: this.getMenuId(formValues.items),
        type: formValues.type,
      };

      if (formValues.type === 'fixed') {
        promotionData['promotion_price'] = formValues.discount;
      } else {
        promotionData['promotion_percentage'] = formValues.discount;
      }

      this.dialogRef.close(promotionData);
    } else {
      this.promotionForm.markAllAsTouched();
    }
  }

  getMenuId(menus: any) {
    let menuNames: any = [];
    menus
      .filter((f: any) => f)
      .forEach((menu: any) => {
        menuNames.push(menu.item_id);
      });
    return menuNames;
  }

  selectDay(day: any, event: any) {
    const activeDays = this.promotionForm.get('activeDays') as FormArray;

    this.dayList.forEach((s: any) => {
      if (s.day == event.target.value && event.target.checked) {
        s.isChecked = true;
        activeDays.push(this.fb.control(day.day));
      } else if (s.day == event.target.value && !event.target.checked) {
        s.isChecked = false;
        const index = activeDays.controls.findIndex((control) => control.value === day.day);
        if (index >= 0) activeDays.removeAt(index);
      }
    });
  }
}
